import React from 'react';
import { useDroppable, useDndContext } from '@dnd-kit/core';
import { Box, Typography } from '@mui/material';
import { PostAdd as PostAddIcon } from '@mui/icons-material';

const DropZone = ({ index, text, isTargetForHoveredComponent }) => {
  const { active } = useDndContext();

  // Check if we're dragging a new component from the palette
  const isDraggingFromPalette = active && active.id.startsWith('palette-');

  const { setNodeRef, isOver } = useDroppable({
    id: `drop-zone-${index}`,
    data: {
      type: 'drop-zone',
      index,
    },
    disabled: !isDraggingFromPalette, // Only accept drops when dragging from palette
  });

  // Always show all drop zones when dragging from palette, highlight only the active one
  const shouldShow = text || isDraggingFromPalette;
  const isHighlighted = (isOver && isDraggingFromPalette) || isTargetForHoveredComponent;
  // At rest (no active drag, not the empty-state) show a subtle insert hint so
  // users discover they can drop between components.
  const isResting = !text && !isDraggingFromPalette && !isHighlighted;

  return (
    <Box
      ref={setNodeRef}
      className={`drop-zone ${isHighlighted ? 'drag-over' : ''} ${!shouldShow && !isResting ? 'hidden' : ''}`}
      sx={{
        minHeight: shouldShow ? (text ? 120 : (isHighlighted ? 32 : 12)) : (isResting ? 8 : 2),
        border: isHighlighted ? '2px dashed' : (shouldShow && isDraggingFromPalette ? '1px dashed #e0e0e0' : 'none'),
        borderColor: isHighlighted ? 'primary.main' : undefined,
        borderRadius: 1,
        display: shouldShow ? 'flex' : 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isHighlighted ? 'rgba(79, 70, 229, 0.1)' : (shouldShow && isDraggingFromPalette ? 'rgba(0, 0, 0, 0.02)' : 'transparent'),
        margin: text ? 2 : (shouldShow ? '4px 0' : '1px 0'),
        transition: 'all 0.2s ease',
        position: 'relative',
        opacity: shouldShow ? 1 : (isResting ? 1 : 0),
        pointerEvents: isDraggingFromPalette ? 'auto' : 'none',
      }}
    >
      {text ? (
        <Box textAlign="center" color="text.secondary" p={3}>
          <PostAddIcon sx={{ fontSize: 48, mb: 2, color: 'primary.main', opacity: 0.7 }} />
          <Typography variant="h6" gutterBottom>
            Drop components here to build your flow
          </Typography>
          <Typography variant="body2">
            Drag components from the left panel to start building your WhatsApp Flow
          </Typography>
        </Box>
      ) : isHighlighted && isDraggingFromPalette ? (
        <Typography variant="caption" color="primary" sx={{
          position: 'absolute',
          left: 8,
          backgroundColor: 'white',
          px: 1,
          borderRadius: 0.5,
          fontSize: 11,
          fontWeight: 500,
        }}>
{isTargetForHoveredComponent ? 'Drop after this component' : index === 0 ? 'Drop at beginning' : 'Drop here'}
        </Typography>
      ) : isResting ? (
        // Faint center line hinting that components can be inserted here
        <Box
          sx={{
            width: '100%',
            height: '1px',
            backgroundColor: 'primary.main',
            opacity: 0.12,
            borderRadius: 1,
          }}
        />
      ) : null}
    </Box>
  );
};

export default DropZone;